import { IconButton, Stack, darken, styled } from '@mui/material';
import { omitForwardedProps } from 'common/utils/omitForwardedProps';

import { isColorWhiteListed } from '../Button/utils/isColorWhiteListed';
import { IconButtonVariant, IconButtonVariantColor } from './types';
import {
  mapActiveBackgroundForTextVariant,
  mapActiveColorForTextVariant,
  mapBackgroundColorForOutlinedActiveVariant,
  mapBackgroundColorForOutlinedFocusVariant,
  mapBackgroundColorForOutlinedHoverVariant,
  mapBorderColorForOutlinedActiveVariant,
  mapBorderColorForOutlinedFocusVariant,
  mapBorderColorForOutlinedVariant,
  mapColorForOutlinedVariant,
  mapColorForTextVariant,
  mapFocusBackgroundForTextVariant,
  mapFocusColorForTextVariant,
  mapHoverBackgroundForTextVariant,
  mapHoverColorForTextVariant,
} from './utils';

export const StyledIconButton = styled(IconButton, {
  shouldForwardProp: omitForwardedProps('isLoading', 'variant', 'iconColor'),
})<{
  isLoading?: boolean;
  variant?: IconButtonVariant;
  iconColor?: IconButtonVariantColor;
}>(({ isLoading, variant, theme: { palette }, iconColor, size }) => ({
  ...(iconColor &&
    isColorWhiteListed(iconColor) &&
    isLoading && {
      '&:disabled': {
        backgroundColor: palette[iconColor].main,
        color: palette[iconColor].contrastText,
      },
    }),
  ...(variant === 'text' && {
    backgroundColor: 'transparent',
    color: mapColorForTextVariant(palette, iconColor),

    '&:disabled': {
      backgroundColor: 'transparent',
      color: palette.grey[300],
    },
    '&:hover': {
      backgroundColor: mapHoverBackgroundForTextVariant(palette, iconColor),
      color: mapHoverColorForTextVariant(palette, iconColor),
    },
    '&:focus': {
      backgroundColor: mapFocusBackgroundForTextVariant(palette, iconColor),
      color: mapFocusColorForTextVariant(palette, iconColor),
    },
    '&:active, &.active': {
      color: mapActiveColorForTextVariant(palette, iconColor),
      backgroundColor: mapActiveBackgroundForTextVariant(palette, iconColor),
    },
  }),
  ...(variant === 'outlined' && {
    backgroundColor: palette.common.white,
    color: mapColorForOutlinedVariant(palette, iconColor),
    border: '1px solid',
    borderColor: mapBorderColorForOutlinedVariant(palette, iconColor),

    '&:disabled': {
      backgroundColor: palette.common.white,
    },
    '&:hover': {
      backgroundColor: mapBackgroundColorForOutlinedHoverVariant(palette, iconColor),
    },
    '&:focus': {
      backgroundColor: mapBackgroundColorForOutlinedFocusVariant(palette, iconColor),
      borderColor: mapBorderColorForOutlinedFocusVariant(palette, iconColor),
    },
    '&:active, &.active': {
      color: mapActiveColorForTextVariant(palette, iconColor),
      backgroundColor: mapBackgroundColorForOutlinedActiveVariant(palette, iconColor),
      borderColor: mapBorderColorForOutlinedActiveVariant(palette, iconColor),
    },
  }),
  ...(variant === 'plain' && {
    backgroundColor: 'transparent',
    color: palette.text.secondary,

    '&:disabled': {
      backgroundColor: 'transparent',
      color: palette.grey[300],
    },
    '&:hover': {
      backgroundColor: 'transparent',
      color: darken(palette.text.secondary, 0.3),
    },
    '&:focus': {
      backgroundColor: palette.extendedPrimary[25],
    },
    '&:active, &.active': {
      backgroundColor: 'transparent',
    },
  }),
  ...(variant === 'contained' &&
    iconColor === 'secondary' && {
      backgroundColor: palette.grey[100],
      color: palette.grey[700],

      '&:hover': {
        backgroundColor: palette.grey[200],
      },

      '&:active, &.active': {
        backgroundColor: palette.extendedPrimary[50],
        color: palette.primary.main,
      },
    }),
  ...(size === 'small' && {
    width: 20,
    height: 20,
    padding: 0,
    '& > .MuiSvgIcon-root': {
      fontSize: '20px',
    },
  }),
}));

export const LoadingIndicatorContainer = styled(Stack)({
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
});
